
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect } from "react";
import Index from "./pages/Index";
import WhySellToNoaPage from "./pages/WhySellToNoaPage";
import AboutNoaPage from "./pages/AboutNoaPage";
import ContactPage from "./pages/ContactPage";
import PlaygroundPage from "./pages/PlaygroundPage";
import OnePagerPage from "./pages/OnePagerPage";
import DeckPage from "./pages/DeckPage";
import TeaserPage from "./pages/TeaserPage";
import LatAmPage from "./pages/LatAmPage";
import NotFound from "./pages/NotFound";
import PostMergerPlaybookPage from "./pages/PostMergerPlaybookPage";
import SerialAcquisitionPage from "./pages/SerialAcquisitionPage";
import NoaOperatingPlatform from "./pages/NoaOperatingPlatform";
import NoaLabsPage from "./pages/NoaLabsPage";
import { LanguageProvider } from "./contexts/LanguageContext";

const RedirectToNoa = () => {
  useEffect(() => {
    const host = window.location.hostname;
    if (host === "noapartners.com" || host === "www.noapartners.com") {
      window.location.href = "https://www.noa.inc";
    }
  }, []);

  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      meta: {
        onError: (error: Error) => {
          console.error('Query error:', error);
        }
      }
    },
    mutations: {
      meta: {
        onError: (error: Error) => {
          console.error('Mutation error:', error);
        }
      }
    }
  }
});

const App = () => {
  useEffect(() => {
    try {
      const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
      link.type = 'image/png';
      link.rel = 'shortcut icon';
      link.href = '/lovable-uploads/85688b30-7d07-4450-9981-844a9bded599.png';
      link.setAttribute('sizes', '32x32');
      document.getElementsByTagName('head')[0].appendChild(link);
    } catch (error) {
      console.error('Failed to set favicon:', error);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <RedirectToNoa />
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/teaser" element={<TeaserPage />} />
              <Route path="/why-sell-to-noa" element={<WhySellToNoaPage />} />
              <Route path="/about" element={<AboutNoaPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/playground" element={<PlaygroundPage />} />
              <Route path="/one-pager" element={<OnePagerPage />} />
              <Route path="/deck" element={<DeckPage />} />
              <Route path="/latam" element={<LatAmPage />} />
              <Route path="/post-merger-playbook" element={<PostMergerPlaybookPage />} />
              <Route path="/serial-acquisition" element={<SerialAcquisitionPage />} />
              <Route path="/operating-platform" element={<NoaOperatingPlatform />} />
              <Route path="/labs" element={<NoaLabsPage />} />
              <Route path="/copy" element={<Navigate to="/" replace />} />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
